import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type UIEvent } from 'react';
import { clsx } from 'clsx';

import styles from './DataTable.module.css';
import { useVirtualScroll } from './hooks/useVirtualScroll';

type DataTablePrimitive = string | number | boolean | null | undefined;
type DataTableRow = Record<string, DataTablePrimitive>;
export type DataTableColumnContent<TRow extends object> = ReactNode | ((row: TRow) => ReactNode);
type DataTableColumn<TRow extends object> = {
  key: Extract<keyof TRow, string>;
  label?: ReactNode | ((row: TRow) => ReactNode);
  cell?: ReactNode | ((row: TRow) => ReactNode);
  fixedWidth?: string;
};
type DataRow<TRow extends object> = { visibleRows: Partial<TRow>[]; rowsToRender: TRow[] };

export type DataTableProps<TRow extends { id: string | number } = DataTableRow & { id: string | number }> = {
  /** The data to be displayed in the table */
  data: TRow[];
  /** The columns configuration for the table */
  columns?: DataTableColumn<TRow>[];
  /** Whether the table header should be sticky */
  stickyHeader?: boolean;
  /** Virtualization settings for the table */
  virtualize?: {
    /** The height of each row in pixels */
    rowHeight: number;
    /** The number of additional rows to render above and below the visible area for smoother scrolling */
    buffer?: number;
  } | null;
  /** The keys in the data objects that indicate whether a row is active (for styling purposes) */
  activeKeys?: (string | number)[];
  /** Message to display when there is no data */
  noDataMessage?: string;
  /** Disable hover styling on rows */
  noHover?: boolean;
  /** Callback for when a row is clicked, only works if noHover is false */
  onRowClick?: (row: TRow) => void;
};

const DEFAULT_BUFFER = 4;

export const DataTable = <TRow extends { id: string | number } = DataTableRow & { id: string | number }>({
  data,
  columns,
  stickyHeader = false,
  virtualize = null,
  activeKeys = [],
  noDataMessage = 'No data to display',
  noHover = false,
  onRowClick,
}: DataTableProps<TRow>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLTableSectionElement>(null);
  const headerRowRef = useRef<HTMLTableRowElement>(null);
  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  const { visibleItems, totalHeight, offsetTop, onScroll, setContainerHeight } = useVirtualScroll({
    rowHeight: virtualize?.rowHeight ?? 0,
    buffer: virtualize?.buffer ?? DEFAULT_BUFFER,
    totalItems: data.length,
  });
  const throttling = useRef(false);
  const resizeThrottling = useRef(false);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);

  const tableData: DataRow<TRow> = useMemo(() => {
    if (!columns) return { visibleRows: data, rowsToRender: data };
    const rowsToRender = virtualize ? visibleItems.map((index) => data[index]) : data;
    const visibleRows = rowsToRender.map((row) => {
      const newRow: Partial<TRow> = {};
      columns.forEach((col) => {
        newRow[col.key] = row[col.key];
      });
      return newRow;
    });
    return { visibleRows, rowsToRender };
  }, [columns, data, virtualize, visibleItems]);

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (!virtualize) return;

      const scrollTop = event.currentTarget.scrollTop;

      if (!throttling.current) {
        throttling.current = true;

        requestAnimationFrame(() => {
          onScroll({ currentTarget: { scrollTop } } as UIEvent<HTMLElement>);
          throttling.current = false;
        });
      }
    },
    [virtualize, onScroll]
  );

  const onRenderValue = (value: ((row: TRow) => ReactNode) | ReactNode, row?: TRow) =>
    typeof value === 'function' ? value(row!) : value;

  useEffect(() => {
    if (!virtualize || !containerRef.current) {
      return;
    }

    const handleResize = () => {
      const containerHeight = containerRef.current?.parentElement?.clientHeight ?? 0;

      if (!resizeThrottling.current) {
        resizeThrottling.current = true;

        requestAnimationFrame(() => {
          setContainerHeight(containerHeight);
          resizeThrottling.current = false;
        });
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [virtualize, setContainerHeight]);

  useEffect(() => {
    if (containerRef.current) {
      setContainerHeight(containerRef.current.parentElement?.clientHeight ?? 0);
    }
  }, [setContainerHeight]);

  useEffect(() => {
    containerRef.current?.scrollTo({ top: 0 });
  }, [data]);

  useEffect(() => {
    if (!stickyHeader || !tbodyRef.current) return;

    const measureWidths = () => {
      const firstRow = tbodyRef.current?.querySelector('tr');
      if (!firstRow) return;

      const cells = firstRow.querySelectorAll('td');
      const widths = Array.from(cells).map((cell) => cell.getBoundingClientRect().width);

      setColumnWidths(widths);
    };

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(measureWidths);
    });

    observer.observe(tbodyRef.current);

    measureWidths();

    return () => observer.disconnect();
  }, [stickyHeader, tableData.visibleRows]);

  return data.length ? (
    <div className={styles['data-table__container']} onScroll={handleScroll} ref={containerRef}>
      {/* sticky header if virtualized  */}
      {virtualize && stickyHeader && columns && (
        <table className={styles['data-table--virtualized']}>
          <thead className={styles['data-table__sticky-header']}>
            <tr className={styles['data-table__header-row']} ref={headerRowRef}>
              {columns &&
                columns.map((column, index) => {
                  const forcedWidth = columnWidths[index] ? `${columnWidths[index]}px` : column.fixedWidth;
                  return (
                    <th
                      key={column.key}
                      className={styles['data-table__cell']}
                      style={{
                        width: forcedWidth,
                        minWidth: forcedWidth,
                        maxWidth: forcedWidth,
                      }}
                    >
                      {onRenderValue(column?.label)}
                    </th>
                  );
                })}
            </tr>
          </thead>
        </table>
      )}

      <div className={styles['data-table__spacer']} style={{ height: totalHeight }} />
      <table
        className={styles['data-table']}
        style={virtualize ? { transform: `translateY(${offsetTop}px)` } : undefined}
      >
        {/* header */}
        <thead className={clsx({ [styles['data-table__sticky-header']]: stickyHeader })} ref={headerRef}>
          <tr className={styles['data-table__header-row']}>
            {!virtualize || !stickyHeader ? (
              columns &&
              columns.map((column, index) => {
                const forcedWidth = columnWidths[index] ? `${columnWidths[index]}px` : column.fixedWidth;
                return (
                  <th key={column.key} className={styles['data-table__cell']} style={{ width: forcedWidth }}>
                    {onRenderValue(column?.label)}
                  </th>
                );
              })
            ) : (
              <th colSpan={columns?.length} style={{ height: headerRowRef.current?.clientHeight }} />
            )}
          </tr>
        </thead>

        {/* data rows */}
        <tbody ref={tbodyRef}>
          {tableData.visibleRows.map((row, index) => (
            <tr
              key={tableData.rowsToRender[index].id}
              className={clsx(styles['data-table__row'], {
                [styles['data-table__row--active']]: activeKeys.includes(tableData.rowsToRender[index].id),
                [styles['data-table__row--hoverable']]: !noHover,
              })}
              onClick={!noHover ? () => onRowClick?.(tableData.rowsToRender[index]) : undefined}
            >
              {Object.entries(row as Record<string, unknown>).map(([key]) => {
                const column = columns?.find((col) => col.key === key);
                return (
                  <td
                    key={key}
                    className={styles['data-table__cell']}
                    style={{
                      width: column?.fixedWidth,
                      height: virtualize ? virtualize.rowHeight : undefined,
                      maxHeight: virtualize ? virtualize.rowHeight : undefined,
                    }}
                  >
                    {onRenderValue(column?.cell, tableData.rowsToRender[index])}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <div className={styles['data-table__empty-state']}>{noDataMessage}</div>
  );
};
